/**
 * ADMIN LICENSE SERVICE
 *
 * Service for super admins to manage licenses across all companies
 * Includes CRUD operations, stats, and device management
 */

import { supabase } from '@/lib/supabase';

// =====================================================
// RAW DATABASE TYPES (snake_case for Supabase)
// =====================================================

export interface RawCompanyLicense {
  id: string;
  company_id: string;
  plan_id: string;
  license_key: string;
  license_type: 'desktop' | 'cloud' | 'hybrid';
  status: 'active' | 'expired' | 'suspended' | 'trial';
  issued_at: string;
  activated_at: string | null;
  expires_at: string;
  last_verified_at: string | null;
  max_devices: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface RawLicensePlan {
  id: string;
  name: string;
  tier_level: number;
  max_users: number;
  max_invoices_per_month: number;
  enabled_features: string[];
  price_monthly_dzd: number;
  price_yearly_dzd: number;
  is_active: boolean;
  created_at: string;
}

export interface RawCompany {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
}

export interface RawLicenseActivation {
  id: string;
  license_id: string;
  device_fingerprint: string;
  device_name: string;
  os_info: string;
  activated_at: string;
  last_seen_at: string;
  is_active: boolean;
}

// =====================================================
// REQUEST/RESPONSE TYPES
// =====================================================

export interface CreateLicenseRequest {
  companyId: string;
  planId: string;
  licenseType: 'desktop' | 'cloud' | 'hybrid';
  durationMonths: number;
  maxDevices: number;
  customKey?: string;
  notes?: string;
}

export interface UpdateLicenseRequest {
  status?: 'active' | 'expired' | 'suspended' | 'trial';
  expiresAt?: Date;
  maxDevices?: number;
  notes?: string;
}

export interface LicenseFilters {
  search?: string; // Search in company name or license key
  status?: 'active' | 'expired' | 'suspended' | 'trial' | 'all';
  planId?: string;
  licenseType?: 'desktop' | 'cloud' | 'hybrid' | 'all';
}

export interface LicenseStats {
  totalLicenses: number;
  activeLicenses: number;
  expiredLicenses: number;
  suspendedLicenses: number;
  trialLicenses: number;
  totalRevenue: number;
  monthlyRevenue: number;
}

export interface CompanyOwner {
  id: string;
  email: string;
  full_name: string;
  role: string;
}

export interface LicenseWithDetails extends RawCompanyLicense {
  company?: RawCompany | null;
  plan?: RawLicensePlan | null;
  activationsCount?: number;
  companyOwner?: CompanyOwner | null;
}

export interface CompanyWithOwner extends RawCompany {
  owner?: CompanyOwner;
}

// =====================================================
// LICENSE CRUD OPERATIONS
// =====================================================

export const AdminLicenseService = {
  /**
   * Get all licenses with optional filtering
   */
  async getAllLicenses(filters?: LicenseFilters): Promise<LicenseWithDetails[]> {
    let query = supabase
      .from('company_licenses')
      .select(`
        *,
        company:companies(id, name, email),
        plan:license_plans(id, name, tier_level)
      `)
      .order('created_at', { ascending: false });

    // Apply filters
    if (filters?.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    if (filters?.planId) {
      query = query.eq('plan_id', filters.planId);
    }

    if (filters?.licenseType && filters.licenseType !== 'all') {
      query = query.eq('license_type', filters.licenseType);
    }

    if (filters?.search) {
      // Search in license_key or company name
      query = query.or(`license_key.ilike.%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Get activation counts for each license
    const licensesWithCounts = await Promise.all(
      (data || []).map(async (license: any) => {
        const { count } = await supabase
          .from('license_activations')
          .select('*', { count: 'exact', head: true })
          .eq('license_id', license.id)
          .eq('is_active', true);

        return {
          ...license,
          activationsCount: count || 0,
        } as LicenseWithDetails;
      })
    );

    return licensesWithCounts;
  },

  /**
   * Get single license by ID with full details
   */
  async getLicenseById(id: string): Promise<LicenseWithDetails> {
    const { data, error } = await supabase
      .from('company_licenses')
      .select(`
        *,
        company:companies(*),
        plan:license_plans(*)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    const licenseData = data as any;

    // Get activation count
    const { count } = await supabase
      .from('license_activations')
      .select('*', { count: 'exact', head: true })
      .eq('license_id', id)
      .eq('is_active', true);

    // Get company owner (user with role 'owner')
    let companyOwner = null;
    if (licenseData.company_id) {
      const { data: ownerData } = await supabase
        .from('users')
        .select('id, email, full_name, role')
        .eq('company_id', licenseData.company_id)
        .eq('role', 'owner')
        .single();
      companyOwner = ownerData;
    }

    return {
      ...licenseData,
      activationsCount: count || 0,
      companyOwner,
    } as LicenseWithDetails;
  },

  /**
   * Create new license for a company
   */
  async createLicense(request: CreateLicenseRequest): Promise<RawCompanyLicense> {
    const { data, error } = await supabase.rpc('create_company_license', {
      p_company_id: request.companyId,
      p_plan_id: request.planId,
      p_license_type: request.licenseType,
      p_duration_months: request.durationMonths,
      p_max_devices: request.maxDevices,
      p_custom_key: request.customKey || null,
    });

    if (error) throw error;

    const licenseData = data as any;

    // Update notes if provided
    if (request.notes && licenseData.id) {
      const { error: updateError } = await supabase
        .from('company_licenses')
        .update({ notes: request.notes })
        .eq('id', licenseData.id);

      if (updateError) throw updateError;
    }

    return licenseData as RawCompanyLicense;
  },

  /**
   * Update existing license
   */
  async updateLicense(id: string, updates: UpdateLicenseRequest): Promise<RawCompanyLicense> {
    const dbUpdates: any = {};
    if (updates.status) dbUpdates.status = updates.status;
    if (updates.expiresAt) dbUpdates.expires_at = updates.expiresAt.toISOString();
    if (updates.maxDevices) dbUpdates.max_devices = updates.maxDevices;
    if (updates.notes) dbUpdates.notes = updates.notes;

    const { data, error } = await supabase
      .from('company_licenses')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as RawCompanyLicense;
  },

  /**
   * Extend license expiration date
   */
  async extendLicense(id: string, newExpiryDate: Date): Promise<RawCompanyLicense> {
    const { data, error } = await supabase
      .from('company_licenses')
      .update({ expires_at: newExpiryDate.toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as RawCompanyLicense;
  },

  /**
   * Suspend a license
   */
  async suspendLicense(id: string, reason: string): Promise<void> {
    const { error } = await supabase
      .from('company_licenses')
      .update({
        status: 'suspended',
        notes: reason,
      })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Reactivate a suspended license
   */
  async reactivateLicense(id: string): Promise<void> {
    const { error } = await supabase
      .from('company_licenses')
      .update({ status: 'active' })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Revoke/delete a license
   */
  async revokeLicense(id: string, reason: string): Promise<void> {
    // First update with reason
    await this.suspendLicense(id, `REVOKED: ${reason}`);

    // Deactivate all devices
    const { error } = await supabase
      .from('license_activations')
      .update({ is_active: false })
      .eq('license_id', id);

    if (error) throw error;
  },

  // =====================================================
  // LICENSE STATS
  // =====================================================

  /**
   * Get overall license statistics
   */
  async getLicenseStats(): Promise<LicenseStats> {
    // Get all licenses
    const { data: licenses, error } = await supabase
      .from('company_licenses')
      .select('*, plan:license_plans(*)');

    if (error) throw error;

    const now = new Date();
    const stats: LicenseStats = {
      totalLicenses: licenses?.length || 0,
      activeLicenses: 0,
      expiredLicenses: 0,
      suspendedLicenses: 0,
      trialLicenses: 0,
      totalRevenue: 0,
      monthlyRevenue: 0,
    };

    (licenses || []).forEach((license: any) => {
      // Count by status
      if (license.status === 'active' && new Date(license.expires_at) > now) {
        stats.activeLicenses++;
      } else if (license.status === 'expired' || new Date(license.expires_at) <= now) {
        stats.expiredLicenses++;
      } else if (license.status === 'suspended') {
        stats.suspendedLicenses++;
      } else if (license.status === 'trial') {
        stats.trialLicenses++;
      }

      // Calculate revenue (assuming yearly licenses)
      if (license.plan) {
        stats.totalRevenue += license.plan.price_yearly_dzd || 0;
        stats.monthlyRevenue += license.plan.price_monthly_dzd || 0;
      }
    });

    return stats;
  },

  // =====================================================
  // LICENSE ACTIVATIONS
  // =====================================================

  /**
   * Get all activations for a license
   */
  async getLicenseActivations(licenseId: string): Promise<RawLicenseActivation[]> {
    const { data, error } = await supabase
      .from('license_activations')
      .select('*')
      .eq('license_id', licenseId)
      .order('activated_at', { ascending: false });

    if (error) throw error;
    return (data || []) as RawLicenseActivation[];
  },

  /**
   * Deactivate a specific device
   */
  async deactivateDevice(licenseId: string, deviceFingerprint: string): Promise<void> {
    const { error } = await supabase
      .from('license_activations')
      .update({ is_active: false })
      .eq('license_id', licenseId)
      .eq('device_fingerprint', deviceFingerprint);

    if (error) throw error;
  },

  // =====================================================
  // LICENSE PLANS
  // =====================================================

  /**
   * Get all license plans
   */
  async getAllPlans(): Promise<RawLicensePlan[]> {
    const { data, error } = await supabase
      .from('license_plans')
      .select('*')
      .order('tier_level', { ascending: true });

    if (error) throw error;
    return (data || []) as RawLicensePlan[];
  },

  /**
   * Get single plan by ID
   */
  async getPlanById(id: string): Promise<RawLicensePlan> {
    const { data, error } = await supabase
      .from('license_plans')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as RawLicensePlan;
  },

  /**
   * Create new license plan
   */
  async createPlan(plan: Omit<RawLicensePlan, 'id' | 'created_at'>): Promise<RawLicensePlan> {
    const { data, error } = await supabase
      .from('license_plans')
      .insert(plan)
      .select()
      .single();

    if (error) throw error;
    return data as RawLicensePlan;
  },

  /**
   * Update license plan
   */
  async updatePlan(id: string, updates: Partial<RawLicensePlan>): Promise<RawLicensePlan> {
    const { data, error } = await supabase
      .from('license_plans')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as RawLicensePlan;
  },

  /**
   * Toggle plan active status
   */
  async togglePlanStatus(id: string, isActive: boolean): Promise<RawLicensePlan> {
    const { data, error } = await supabase
      .from('license_plans')
      .update({ is_active: isActive })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as RawLicensePlan;
  },

  // =====================================================
  // COMPANIES
  // =====================================================

  /**
   * Get all companies for license assignment (with owner info)
   */
  async getAllCompanies(): Promise<CompanyWithOwner[]> {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .order('name', { ascending: true });

    if (error) throw error;

    // Get owner for each company
    const companiesWithOwners = await Promise.all(
      (data || []).map(async (company: any) => {
        const { data: ownerData } = await supabase
          .from('users')
          .select('id, email, full_name, role')
          .eq('company_id', company.id)
          .eq('role', 'owner')
          .single();

        return {
          ...company,
          owner: ownerData || undefined,
        } as CompanyWithOwner;
      })
    );

    return companiesWithOwners;
  },
};
